import { useCallback, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxfReportsV1ReportCommandServiceReportDownload } from '@interxfi/api-client/generated/hooks/ixf-reports-v1-report-command-service-report-download';
import { useIxfReportsV1ReportQueryServiceReportGet } from '@interxfi/api-client/generated/hooks/ixf-reports-v1-report-query-service-report-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { Typography } from '@interxfi/design-system/components/typography';
import DownloadIcon from '@interxfi/design-system/icons/react/icon-download';
import type { IxfReportsV1ReportState, IxfReportsV1ReportStatus } from '@interxfi/types/generated/api';
import { downloadFromUrl } from '../../../../../../../../../../features/document/utils';
import { ReportEventDrawer, ReportEventSummary } from '../../../../../../../../../../features/report/report-event';
import { getReportStateListItems } from '../../../../../../../../../../features/report/report-list-items';
import { getReportDetailPath } from '../../../../../../../../../../features/report/report-routing';
import { ReportStatusPill } from '../../../../../../../../../../features/report/report-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/reporting/archive/_report/$reportId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `report-${params.reportId}`,
        label: 'Report',
        to: getReportDetailPath(params.reportId),
        suffix: <CopyButton content={params.reportId} label="Copy Report ID" tooltip={{ copy: params.reportId, copied: 'Copied Report ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Report'),
  component: ReportDetail,
});

function getListItems(data: IxfReportsV1ReportState | undefined) {
  const items = getReportStateListItems(data);

  return {
    general: {
      left: [
        items.data.spec.type,
        ...(items.data.spec.startDate ? [items.data.spec.startDate] : []),
        ...(items.data.spec.endDate ? [items.data.spec.endDate] : []),
        items.data.spec.storageBucket,
        items.data.spec.storageKey,
        ...(items.data.spec.replaces ? [items.data.spec.replaces] : []),
        ...(items.data.replacedBy ? [items.data.replacedBy] : []),
      ] satisfies ReadonlyListItem[],
      right: [items.metadata.createdAt, items.metadata.updatedAt, items.reportId] satisfies ReadonlyListItem[],
    },
  };
}

function getMenuItems(
  reportId: string,
  status: IxfReportsV1ReportStatus | undefined,
  downloadReport: VoidFunction,
  isDownloading: boolean,
): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: ReportEventDrawer,
        props: { request: { reportId } },
      },
    },
    actions: status
      ? [
          {
            id: 'download',
            icon: <DownloadIcon aria-hidden />,
            label: 'Download',
            disabled: isDownloading,
            onClick: downloadReport,
          },
        ]
      : [],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load report', 'view this report');
const downloadErrorMessageGetter = buildDefaultErrorMessageGetter('Failed to download report', 'download this report');

function ReportDetail() {
  const { reportId } = Route.useParams();
  const request = { reportId } as const;
  const { data, error, isPending } = useIxfReportsV1ReportQueryServiceReportGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const { mutateAsync: downloadReport, error: downloadError, isPending: isDownloading } = useIxfReportsV1ReportCommandServiceReportDownload();
  useErrorHandler(downloadError, downloadErrorMessageGetter);
  const handleDownloadReport = useCallback(async () => {
    try {
      const res = await downloadReport({ reportId });

      if (res?.url) {
        downloadFromUrl(res.url);
      }
    } catch {}
  }, [downloadReport, reportId]);

  const listItems = useMemo(() => getListItems(data?.report), [data?.report]);
  const menuItems = useMemo(
    () => getMenuItems(reportId, data?.report?.status, handleDownloadReport, isDownloading),
    [reportId, data?.report?.status, handleDownloadReport, isDownloading],
  );

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <ReportStatusPill isPending={showPending} size="large" status={data?.report?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <ReportEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
